import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, AlertTriangle, Loader2, Calendar } from "lucide-react";
import { apiGet, formatLKR } from "@/lib/api";

const Row = ({ label, expected, actual, diff, formatter = formatLKR }) => {
  const positive = (diff || 0) >= 0;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 py-3 border-b last:border-0">
      <div className="space-y-0.5">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      </div>
      <div className="space-y-0.5">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Expected</div>
        <div className="font-medium tabular">{formatter(expected)}</div>
      </div>
      <div className="space-y-0.5">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Actual</div>
        <div className="font-medium tabular">
          {formatter(actual)}{" "}
          {diff != null && diff !== 0 && (
            <span
              className={`ml-1 text-[11px] ${
                positive ? "text-[hsl(var(--success))]" : "text-[hsl(var(--destructive))]"
              }`}
            >
              ({positive ? "+" : ""}
              {formatter(diff)})
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const ExpectedVsActualCard = ({ portfolioId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    apiGet(`/portfolios/${portfolioId}/projection/expected-vs-actual`)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.detail || e?.message || "Failed");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [portfolioId]);

  return (
    <Card className="border bg-card" data-testid="expected-vs-actual-card">
      <CardHeader className="pb-3">
        <CardTitle className="font-display text-base flex items-center gap-2">
          <Calendar className="h-4 w-4 text-[hsl(var(--primary))]" />
          Expected vs Actual
          {data?.too_recent && (
            <Badge variant="secondary" className="text-[10px]">
              Holdings too recent for meaningful comparison
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Tracks how the portfolio is performing relative to the base-case projection from
          each holding's purchase date.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-3">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {error && <div className="text-sm text-destructive">{error}</div>}
        {data && !loading && (
          <>
            {data.too_recent ? (
              <div className="rounded-md border border-warning/30 bg-warning/5 p-3 text-xs text-muted-foreground">
                Your oldest holding has only been held for {data.max_years_held?.toFixed(2)}{" "}
                years. Expected-vs-actual comparison becomes useful after ~6 months of
                ownership.
              </div>
            ) : (
              <div data-testid="expected-vs-actual-rows">
                <Row
                  label="Dividend income"
                  expected={data.expected_dividend_income_lkr}
                  actual={data.actual_dividend_income_lkr}
                  diff={data.dividend_difference_lkr}
                />
                <Row
                  label="Portfolio value today"
                  expected={data.expected_portfolio_value_today}
                  actual={data.actual_portfolio_value_today}
                  diff={data.value_difference_lkr}
                />
                <div className="mt-3 flex items-center gap-2 text-sm">
                  {data.on_track ? (
                    <span
                      className="inline-flex items-center gap-1.5 text-[hsl(var(--success))]"
                      data-testid="on-track-badge"
                    >
                      <CheckCircle2 className="h-4 w-4" /> On track
                    </span>
                  ) : (
                    <span
                      className="inline-flex items-center gap-1.5 text-[hsl(var(--warning))]"
                      data-testid="off-track-badge"
                    >
                      <AlertTriangle className="h-4 w-4" /> Review recommended
                    </span>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ExpectedVsActualCard;
